import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { sessionTranscriptsApi } from "../api/sessionTranscriptsApi";
import { SessionTranscripts } from "../types/supabase";

export function useMeetingTranscripts() {
  return useQuery({
    queryKey: ["meeting_transcripts"],
    queryFn: () => sessionTranscriptsApi.getAll(),
  });
}

export function useMeetingTranscriptById(id: string) {
  return useQuery({
    queryKey: ["meeting_transcripts", id],
    queryFn: () => sessionTranscriptsApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateMeetingTranscript() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<SessionTranscripts>) =>
      sessionTranscriptsApi.insert(data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["meeting_transcripts"] }),
  });
}

export function useUpsertMeetingTranscript() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<SessionTranscripts>) =>
      sessionTranscriptsApi.upsert(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["meeting_transcripts"] });
      if (data?.uuid) {
        queryClient.invalidateQueries({
          queryKey: ["meeting_transcripts", data.uuid],
        });
      }
    },
  });
}

export function useMeetingTranscriptRealtime(
  id: string,
  onChange: (payload: SessionTranscripts) => void
) {
  useEffect(() => {
    const subscription = sessionTranscriptsApi.subscribeById(id, onChange);
    return () => {
      subscription.unsubscribe();
    };
  }, [id, onChange]);
}
